import type { CSSProperties, ReactNode } from 'react';
import { ListRow } from './ListRow.tsx';
import { TableFrame } from '../primitives/TableFrame.tsx';

export interface Column<Row> {
  /** Stable key, also used for the React key. */
  id: string;
  header: string;
  /** Right-aligned and tabular. Use for money, counts, times. */
  numeric?: boolean;
  /** Dropped first when the frame narrows, before the card switch. */
  secondary?: boolean;
  /** Uses the primary cell treatment even when the column is not first. */
  strong?: boolean;
  /** Fixed column width from the design-system table definitions. */
  width?: number | string;
  cell: (row: Row) => ReactNode;
}

export interface DataTableProps<Row> {
  caption: string;
  columns: Column<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  /** One card per row, shown when the FRAME is narrow. Falls back to the columns. */
  mobileCard?: (row: Row) => ReactNode;
  /** Trailing cell of actions. Never the only way to reach a row's detail. */
  rowAction?: (row: Row) => ReactNode;
  onRowClick?: (row: Row) => void;
  /** Shown instead of the table when there are no rows. */
  empty?: ReactNode;
  loading?: boolean;
  error?: ReactNode;
}

/**
 * One table definition, two renderings.
 *
 * The prototype wrote every table twice — a desktop `<table>` and a mobile card list,
 * duplicated across nine screens, which is the single biggest source of drift in it.
 * Here the columns are declared once and the card list is derived from them unless a
 * view supplies a better one.
 *
 * The switch is a CONTAINER query on the frame, not a viewport query: the same table is
 * full width on Partners and in a narrow column on a partner-detail tab, so the right
 * trigger is how much room the card has. Both renderings are in the DOM and CSS decides
 * — no view passes a mode down.
 */
export function DataTable<Row>({
  caption,
  columns,
  rows,
  rowKey,
  mobileCard,
  rowAction,
  onRowClick,
  empty,
  loading,
  error,
}: DataTableProps<Row>) {
  if (error) return <>{error}</>;
  if (loading) return <TableSkeleton columns={columns.length} />;
  if (rows.length === 0 && empty) return <>{empty}</>;

  return (
    <TableFrame
      table={
        <table className="table">
          {/* For assistive tech only: every table already sits under a heading that
              names it, so showing this too reads as the title twice. */}
          <caption className="visuallyHidden">{caption}</caption>
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.id}
                  scope="col"
                  className={headerClass(column)}
                  style={columnStyle(column)}
                  {...(column.numeric ? { 'aria-sort': 'none' as const } : {})}
                >
                  {column.header}
                </th>
              ))}
              {rowAction ? (
                <th scope="col" className="tableHeadCell tableHeadAction">
                  <span className="visuallyHidden">Actions</span>
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={rowKey(row)}
                className={onRowClick ? 'tableRowInteractive' : undefined}
                {...(onRowClick ? { onClick: () => onRowClick(row) } : {})}
              >
                {columns.map((column, index) => (
                  <td
                    key={column.id}
                    className={cellClass(column, index === 0)}
                    style={columnStyle(column)}
                  >
                    {column.cell(row)}
                  </td>
                ))}
                {rowAction ? <td className="tableCell tableCellAction">{rowAction(row)}</td> : null}
              </tr>
            ))}
          </tbody>
        </table>
      }
      cards={rows.map((row) => (
        <article key={rowKey(row)} className="tableCard">
          {mobileCard ? (
            mobileCard(row)
          ) : (
            <DerivedCard
              columns={columns}
              row={row}
              actions={rowAction?.(row)}
              {...(onRowClick ? { onClick: () => onRowClick(row) } : {})}
            />
          )}
        </article>
      ))}
    />
  );
}

/**
 * The default card, when a view has not written a better one.
 *
 * Label-and-value pairs in column order. It is never as good as a hand-written card,
 * but it means a new table is readable on a phone from the moment it exists rather
 * than after someone remembers to add the second rendering.
 */
function DerivedCard<Row>({
  columns,
  row,
  actions,
  onClick,
}: {
  columns: Column<Row>[];
  row: Row;
  actions?: ReactNode;
  onClick?: () => void;
}) {
  const [first, ...rest] = columns;

  return (
    <ListRow
      title={first?.cell(row)}
      meta={rest.map((column, index) => (
        <span key={column.id}>
          {index === 0 ? null : ' · '}
          {column.cell(row)}
        </span>
      ))}
      {...(actions === undefined ? {} : { actions })}
      {...(onClick === undefined ? {} : { onClick })}
    />
  );
}

function headerClass<Row>(column: Column<Row>): string {
  return ['tableHeadCell', column.numeric && 'tableCellNumeric', column.secondary && 'colSecondary']
    .filter(Boolean)
    .join(' ');
}

function cellClass<Row>(column: Column<Row>, isFirst: boolean): string {
  return [
    'tableCell',
    (isFirst || column.strong) && 'tableCellPrimary',
    column.numeric && 'tableCellNumeric',
    column.secondary && 'colSecondary',
  ]
    .filter(Boolean)
    .join(' ');
}

function columnStyle<Row>(column: Column<Row>): CSSProperties | undefined {
  return column.width === undefined ? undefined : { width: column.width };
}

/** A shape-preserving placeholder, so the layout does not jump when rows arrive. */
export function TableSkeleton({ columns, rows = 4 }: { columns: number; rows?: number }) {
  return (
    <div className="tableSkeleton" aria-busy="true" aria-live="polite">
      <span className="visuallyHidden">Loading</span>
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div key={rowIndex} className="tableSkeletonRow">
          {Array.from({ length: columns }, (_, cellIndex) => (
            <span key={cellIndex} className="tableSkeletonCell" />
          ))}
        </div>
      ))}
    </div>
  );
}
